import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CompanyProfileDto } from './company-profile.dtos';
import { COMPANY_API_BASE_URL } from './company-api.tokens';
import { companyUrl } from './company.urls';

export interface CheckDomainRequest {
  domain: string;
}

export interface DomainAvailabilityResponse {
  isAvailable: boolean;
  /** Backend error code, e.g. DOMAIN_ALREADY_IN_USE / PUBLIC_EMAIL_PROVIDER. */
  error?: string;
}

/**
 * The account-upgrade endpoints (Personal → Company), shared by both portals.
 *
 * Errors are NOT swallowed here — the upgrade UI maps the backend error codes to specific
 * messages (domain already in use, public provider, DNS validation failed …), which a generic
 * catch would flatten into "something went wrong".
 */
@Injectable({ providedIn: 'root' })
export class CompanyUpgradeApi {
  private http = inject(HttpClient);
  private readonly apiUrl = inject(COMPANY_API_BASE_URL);

  /** Pre-flight: is this domain usable for this account at all? */
  checkDomainAvailability(
    companyId: string,
    domain: string
  ): Observable<DomainAvailabilityResponse> {
    return this.http.post<DomainAvailabilityResponse>(
      `${companyUrl(this.apiUrl, companyId)}/check-domain`,
      { domain }
    );
  }

  /** Activate the tenant as a company account (requires the DNS proof to have succeeded). */
  upgradeToCompany(companyId: string, domain: string): Observable<CompanyProfileDto> {
    return this.http.post<CompanyProfileDto>(
      `${companyUrl(this.apiUrl, companyId)}/upgrade-to-company`,
      { domain }
    );
  }

  /**
   * DEV/TEST ONLY: upgrade without the DNS proof. The endpoint itself is gated to non-production
   * environments server-side; the UI additionally only offers it on a non-production host.
   */
  devUpgradeToCompany(domain: string): Observable<CompanyProfileDto> {
    return this.http.post<CompanyProfileDto>(`${this.apiUrl}/dev/devcompanies/upgrade-for-user`, {
      domain,
    });
  }
}
